import { useCallback } from 'react';
import * as Sentry from '@sentry/react';

import { useDriveSetReactionMutation, Drive_Response_Reaction } from '@occc/graphql';

import type { ChatMessage } from '../types';

type UseSetReactionProps = {
  setMessages: (updater: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => void;
};

export const useSetReaction = ({ setMessages }: UseSetReactionProps) => {
  const [setReactionMutation, { loading }] = useDriveSetReactionMutation();

  const handleReactionChange = useCallback(
    async (responseId: string, reaction: Drive_Response_Reaction | null) => {
      let previousReaction: Drive_Response_Reaction | null | undefined;

      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.id === responseId) {
            previousReaction = msg.reaction;
            return { ...msg, reaction };
          }
          return msg;
        }),
      );

      try {
        const result = await setReactionMutation({
          variables: {
            input: {
              responseId,
              reaction,
            },
          },
        });

        const hasGraphQLErrors = Array.isArray(result.errors) && result.errors.length > 0;
        const driveSetReaction = result.data?.driveSetReaction;

        if (hasGraphQLErrors) {
          Sentry.captureException(new Error('GraphQL errors setting reaction'), {
            extra: { errors: result.errors },
            tags: {
              component: 'DriveSetReaction',
              responseId,
            },
          });
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === responseId ? { ...msg, reaction: previousReaction } : msg,
            ),
          );
          return;
        }

        if (!driveSetReaction) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === responseId ? { ...msg, reaction: previousReaction } : msg,
            ),
          );
          return;
        }

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === responseId ? { ...msg, reaction: driveSetReaction.reaction } : msg,
          ),
        );
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            component: 'DriveSetReaction',
            responseId,
          },
        });
        setMessages((prev) =>
          prev.map((msg) => (msg.id === responseId ? { ...msg, reaction: previousReaction } : msg)),
        );
      }
    },
    [setReactionMutation, setMessages],
  );

  return {
    handleReactionChange,
    loading,
  };
};
